import axios, { AxiosError } from "axios";
import type { InternalAxiosRequestConfig } from "axios";
import i18n from "i18next";
import { notify } from "../components/ui/toaster";

const API_BASE_URL = process.env.REACT_APP_BASE_API_URL;

// Per-request opt-outs the interceptor honours.
//   __notifyOnSuccess: i18n key for a "saved/created/deleted" toast on 2xx.
//   __silent:          skip the auto-error toast (caller handles UX inline).
declare module "axios" {
  export interface AxiosRequestConfig {
    __notifyOnSuccess?: string;
    __silent?: boolean;
  }
  export interface InternalAxiosRequestConfig {
    __notifyOnSuccess?: string;
    __silent?: boolean;
  }
}

const extractErrorMessage = (error: AxiosError): string | undefined => {
  const data = error.response?.data as any;
  if (typeof data === "string") return data;
  if (data?.message && typeof data.message === "string") return data.message;
  if (data?.title && typeof data.title === "string") return data.title;
  if (data?.error && typeof data.error === "string") return data.error;
  return error.message;
};

if (!API_BASE_URL) {
  throw new Error("REACT_APP_BASE_API_URL must be set at build time.");
}

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add token to requests if available
apiClient.interceptors.request.use(
  (config) => {
    // Skip auth header for auth endpoints
    if (
      config.url?.includes("/auth/login") ||
      config.url?.includes("/auth/register") ||
      config.url?.includes("/auth/refresh")
    ) {
      return config;
    }

    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Handle response errors and automatic token refresh.
// Also: fire an auto-toast on success when the request opted in via
// __notifyOnSuccess, and on error toasts for non-auth, non-validation failures
// unless the caller passed __silent.
apiClient.interceptors.response.use(
  (response) => {
    const successHint = (response.config as any)?.__notifyOnSuccess;
    if (successHint) {
      notify({
        type: "success",
        title: i18n.t(successHint, { defaultValue: successHint }),
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && originalRequest) {
      // Skip refresh for auth endpoints
      if (
        originalRequest.url?.includes("/auth/login") ||
        originalRequest.url?.includes("/auth/register") ||
        originalRequest.url?.includes("/auth/refresh")
      ) {
        return Promise.reject(error);
      }

      // If we're not on auth pages and have a refresh token, try to refresh
      if (!window.location.pathname.startsWith("/auth")) {
        const refreshToken = localStorage.getItem("refreshToken");

        if (refreshToken && !originalRequest._retry) {
          if (isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return apiClient(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            // Attempt to refresh the token
            const accessToken = localStorage.getItem("accessToken");
            const response = await apiClient.post("/auth/refresh", {
              refreshToken,
              accessToken,
            });
            const {
              token,
              refreshToken: newRefreshToken,
              expiresIn,
              user,
              company,
            } = response.data;

            // Store new tokens
            localStorage.setItem("accessToken", token);
            if (newRefreshToken) {
              localStorage.setItem("refreshToken", newRefreshToken);
            }
            if (expiresIn) {
              const expiryTime = Date.now() + expiresIn * 1000;
              localStorage.setItem("tokenExpiry", expiryTime.toString());
            }
            // Update stored user so company features remain visible after refresh
            if (user && company) {
              localStorage.setItem(
                "user",
                JSON.stringify({ ...user, company }),
              );
            }

            // Update authorization header
            apiClient.defaults.headers.common["Authorization"] =
              `Bearer ${token}`;
            originalRequest.headers.Authorization = `Bearer ${token}`;

            // Process queued requests
            processQueue(null, token);
            isRefreshing = false;

            // Retry original request
            return apiClient(originalRequest);
          } catch (refreshError) {
            // Refresh failed, clear auth and redirect
            processQueue(refreshError, null);
            isRefreshing = false;

            console.error("Token refresh failed:", refreshError);
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("tokenExpiry");
            localStorage.removeItem("user");

            window.location.href = "/auth/sign-in";
            return Promise.reject(refreshError);
          }
        } else {
          // No refresh token available, clear auth and redirect
          console.error("Authentication failed - no refresh token available");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("tokenExpiry");
          localStorage.removeItem("user");

          window.location.href = "/auth/sign-in";
        }
      }
    }

    // Universal error toast for non-auth, non-validation failures.
    //  - 401 already redirects to sign-in above; no toast (would be noise).
    //  - 422 is form-validation; the calling form should render <Field.Error> inline.
    //  - __silent lets a caller opt out for custom error UX.
    const status = error.response?.status;
    const silent = (error.config as any)?.__silent;
    if (!silent && status !== 401 && status !== 422) {
      notify({
        type: "error",
        title: i18n.t("app.errors.requestFailed", {
          defaultValue: "Request failed",
        }),
        description: extractErrorMessage(error),
      });
    }

    return Promise.reject(error);
  },
);

export default apiClient;
